import { Navigate, useLocation } from 'react-router-dom';
import { PartnerEndpoints } from '../../../utils/endpoints';
import { Constants } from '../../../utils/constants';
import { formatNaira, formatCount, formatPercent, grossAmount } from '../../../utils/money';
import { formatLagos } from '../../../utils/datetime';
import PageHeader from '../../shell/PageHeader';
import {
  Card,
  DataTable,
  DateRangeFilter,
  FilterSelect,
  JsonViewer,
  Pagination,
  StatusPill,
  TableCard,
} from '../../ui';
import { useServerTable } from '../../../hooks/useServerTable';

const buildColumns = ({ dateFilter, statusFilter }) => [
  {
    key: 'created_at',
    header: 'Date (WAT)',
    sortKey: 'created_at',
    sortDefault: 'desc',
    filter: dateFilter,
    render: (row) => <span className="whitespace-nowrap">{formatLagos(row.created_at)}</span>,
  },
  { key: 'gross', header: 'Gross', align: 'right', render: (row) => formatNaira(grossAmount(row)) },
  { key: 'discount_amount', header: 'Discount', align: 'right', render: (row) => formatNaira(row.discount_amount) },
  {
    key: 'amount',
    header: 'Amount',
    align: 'right',
    sortKey: 'amount',
    sortDefault: 'desc',
    render: (row) => formatNaira(row.amount),
  },
  { key: 'merchant', header: 'Merchant', render: (row) => row.merchant || '—' },
  {
    key: 'status',
    header: 'Status',
    sortKey: 'status',
    filter: statusFilter,
    render: (row) => <StatusPill status={row.status} />,
  },
  { key: 'reference', header: 'Reference', render: (row) => row.reference || '—' },
];

const Detail = ({ label, children }) => (
  <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-theme-border py-2 dark:border-themedark-border">
    <span className="text-base text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">{label}</span>
    <span className="text-base text-theme-headings dark:text-themedark-headings">{children}</span>
  </div>
);

// has_active_discount stays true with a zero cap, which caps every discount at ₦0.
const isDiscountActive = (discount) =>
  Boolean(discount?.has_active_discount) && Number(discount?.discount_cap) > 0;

const DiscountSummary = ({ discount }) => {
  if (!isDiscountActive(discount)) return <>No active discount</>;

  const minAmount = Number(discount.discount_min_amount) || 0;
  return (
    <>
      {formatPercent(discount.discount_percentage)} up to {formatNaira(discount.discount_cap)}
      {minAmount > 0 && ` on taps from ${formatNaira(minAmount)}`}
    </>
  );
};

const IssuedUserLog = () => {
  const location = useLocation();
  const user = location.state?.user ?? null;
  // The issued-user resource no longer carries the tap list, and the transactions endpoint has no
  // filter[external_user_pan], so a PAN search is the only available narrowing.
  const table = useServerTable(PartnerEndpoints.transactions, {
    search: user?.pan ?? '',
    sort: '-created_at',
    filters: { status: '' },
    enabled: Boolean(user?.pan),
    fallbackError: 'We could not load this user\u2019s transactions. Please try again.',
  });

  if (!user) return <Navigate to="/layout/issued-users" replace />;

  const hasMeta = user.meta !== null && typeof user.meta === 'object' && Object.keys(user.meta).length > 0;

  return (
    <>
      <PageHeader
        title="Issued User Log"
        breadcrumbs={[
          { label: 'Home', to: '/layout/dashboard' },
          { label: 'Issued Users', to: '/layout/issued-users' },
          { label: user.name || user.pan || 'User' },
        ]}
      />

      <Card title={user.name || 'Issued User'} className="mb-4">
        <div className="grid gap-x-8 sm:grid-cols-2">
          <Detail label="PAN">{user.pan || '—'}</Detail>
          <Detail label="Status">
            <StatusPill status={user.status} />
          </Detail>
          <Detail label="Volume">{formatNaira(user.total_transactions)}</Detail>
          <Detail label="External ID">{user.external_id || '—'}</Detail>
          <Detail label="Discount">
            <DiscountSummary discount={user.discount} />
          </Detail>
        </div>

        <p className="mt-3 text-base text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
          Discounts are configured by CashAfrica and cannot be changed from this dashboard.
        </p>

        {hasMeta && (
          <div className="mt-4">
            <h6 className="mb-2 text-f-h6 font-semibold text-theme-headings dark:text-themedark-headings">Metadata</h6>
            <JsonViewer data={user.meta} />
          </div>
        )}
      </Card>

      <TableCard
        title="Cash Taps"
        summary={{ label: 'Showing', value: formatCount(table.total), loading: table.loading }}
        footer={
          table.total > 0 && (
            <Pagination
              page={table.page}
              pageCount={table.pageCount}
              pageSize={table.pageSize}
              onPageChange={table.setPage}
              onPageSizeChange={table.setPageSize}
            />
          )
        }
      >
        <DataTable
          columns={buildColumns({
            dateFilter: (
              <DateRangeFilter
                label="Date"
                from={table.from}
                to={table.to}
                onFromChange={table.setFrom}
                onToChange={table.setTo}
              />
            ),
            statusFilter: (
              <FilterSelect
                value={table.filters.status ?? ''}
                onChange={(value) => table.setFilter('status', value)}
                options={Constants.transactionStatuses}
                label="All"
                ariaLabel="Filter by status"
              />
            ),
          })}
          rows={table.rows}
          rowKey={(row) => row.id ?? row.reference}
          serial
          serialStart={table.page * table.pageSize}
          loading={table.loading}
          error={table.error}
          invalidDateRange={table.invalidDateRange}
          sort={table.sort}
          onSortChange={table.setSort}
          empty={{ title: 'No transactions', message: 'No transactions found for the selected filters.' }}
        />
      </TableCard>
    </>
  );
};

export default IssuedUserLog;
